import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Sparkles,
  HeartHandshake,
  Flame,
  Sun,
  Music,
  HandHelping,
  Sunrise,
  Cloud,
  Compass,
  ChevronLeft,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const beliefs = [
  {
    id: 'bible',
    title: 'The Bible',
    summary: "God's inspired Word — our final authority for faith and life.",
    icon: BookOpen,
    body:
      "We believe the Holy Bible is God's inspired Word. It is completely trustworthy and serves as our guide for faith, salvation, and Christian living.",
    verse: {
      text:
        'All scripture is given by inspiration of God, and is profitable for doctrine, for reproof, for correction, for instruction in righteousness.',
      ref: '2 Timothy 3:16',
    },
  },
  {
    id: 'one-god',
    title: 'One God',
    summary: 'One eternal God, revealed in Jesus Christ.',
    icon: Sparkles,
    body:
      'We believe there is one eternal God who has revealed Himself in Jesus Christ. Jesus is the fullness of the Godhead manifested in the flesh and the Savior of the world.',
    verse: {
      text: 'Jesus Christ… This is the true God, and eternal life.',
      ref: '1 John 5:20',
    },
  },
  {
    id: 'salvation',
    title: 'Salvation',
    summary: 'By grace through faith \u2014 repentance, baptism, and the Holy Ghost.',
    icon: HeartHandshake,
    body:
      'We believe salvation is available to everyone through the grace of God by faith in Jesus Christ. The biblical response to the Gospel includes repentance, water baptism in the name of Jesus Christ for the remission of sins, and receiving the gift of the Holy Ghost.',
    verses: [
      {
        text: 'For by grace are ye saved through faith\u2026 it is the gift of God.',
        ref: 'Ephesians 2:8',
      },
      {
        text:
          'Repent, and be baptized every one of you in the name of Jesus Christ for the remission of sins, and ye shall receive the gift of the Holy Ghost.',
        ref: 'Acts 2:38',
      },
    ],
  },
  {
    id: 'holy-ghost',
    title: 'The Holy Ghost',
    summary: 'A promise still for today \u2014 for you, your children, and all who are called.',
    icon: Flame,
    body:
      'We believe every believer can receive the gift of the Holy Ghost, with the initial biblical evidence of speaking in other tongues as the Spirit gives utterance. This promise is still available today.',
    verse: {
      text:
        'For the promise is unto you, and to your children, and to all that are afar off, even as many as the Lord our God shall call.',
      ref: 'Acts 2:39',
    },
  },
  {
    id: 'holy-living',
    title: 'Holy Living',
    summary: 'A life transformed \u2014 character, conduct, and devotion.',
    icon: Sun,
    body:
      'We believe that following Jesus transforms every area of life. Through the power of the Holy Spirit, believers are called to live lives that honor God in character, conduct, and devotion.',
    verse: {
      text: 'Follow peace with all men, and holiness, without which no man shall see the Lord.',
      ref: 'Hebrews 12:14',
    },
  },
  {
    id: 'worship',
    title: 'Worship',
    summary: 'Sincere, passionate, and Spirit-led.',
    icon: Music,
    body:
      'We believe worship should be sincere, passionate, and Spirit-led. We joyfully worship God through prayer, singing, praise, thanksgiving, and the preaching of His Word.',
    verse: {
      text: 'O clap your hands, all ye people; shout unto God with the voice of triumph.',
      ref: 'Psalm 47:1',
    },
  },
  {
    id: 'healing',
    title: 'Divine Healing',
    summary: 'Jesus still heals \u2014 body, mind, and spirit.',
    icon: HandHelping,
    body:
      'We believe Jesus Christ is the same yesterday, today, and forever. He still heals physically, emotionally, and spiritually, and we believe in praying for the sick with faith and expectation.',
    verse: {
      text: 'They shall lay hands on the sick, and they shall recover.',
      ref: 'Mark 16:18',
    },
  },
  {
    id: 'resurrection',
    title: 'The Resurrection & Eternal Judgment',
    summary: 'Every person will stand before God \u2014 and life is promised in Christ.',
    icon: Sunrise,
    body:
      'We believe there will be a future resurrection of both the saved and the lost. Every person will stand before God, and eternal life is promised to those who have placed their faith in Jesus Christ and obeyed His Gospel.',
    verse: { text: 'See the full passage in Scripture.', ref: 'Revelation 20:11\u201315' },
  },
  {
    id: 'return',
    title: 'The Return of Jesus Christ',
    summary: 'He is coming again \u2014 and we live faithfully until that day.',
    icon: Cloud,
    body:
      'We believe Jesus Christ is coming again for His Church. We eagerly anticipate His return and seek to live faithfully until that day.',
    verse: {
      text:
        'Then we which are alive and remain shall be caught up together with them in the clouds, to meet the Lord in the air: and so shall we ever be with the Lord.',
      ref: '1 Thessalonians 4:17',
    },
  },
];

const Verse = ({ text, ref }) => (
  <div className="mt-4 pl-5 border-l-2 border-[#c8b98a]">
    <p className="italic text-[15px] leading-relaxed text-[#3a3a3a]">“{text}”</p>
    <p className="mt-2 text-[13px] font-semibold tracking-wide text-[#8a7a4a] uppercase">
      {ref}
    </p>
  </div>
);

const WhatWeBelieve = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f8f4ee]">
      {/* Slim brand bar */}
      <header className="bg-[#0a1030] text-white">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-12 py-5">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 group"
          >
            <Flame className="h-6 w-6" strokeWidth={2.5} />
            <span className="font-extrabold tracking-wide text-[14px] md:text-[15px]">
              APOSTOLIC CHRIST TABERNACLE
            </span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-sm text-white/80 hover:text-white transition-colors"
          >
            <ChevronLeft className="h-4 w-4" /> Back to home
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0a1030] via-[#111a4a] to-[#0a1030] text-white">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_top,rgba(200,185,138,0.35),transparent_60%)]" />
        <div className="relative max-w-[1000px] mx-auto text-center px-6 py-20 md:py-28">
          <p className="text-[12px] tracking-[0.3em] uppercase text-[#c8b98a] mb-5">
            About · Our Faith
          </p>
          <h1
            className="text-[46px] md:text-[76px] lg:text-[92px] leading-[1.02]"
            style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
          >
            What We Believe
          </h1>
          <p className="mt-8 text-[17px] md:text-[19px] text-white/85 max-w-[720px] mx-auto leading-relaxed">
            At Apostolic Christ Tabernacle, we believe the Bible is the
            inspired, infallible Word of God and the final authority for our
            faith, doctrine, and daily living. Everything we teach and practice
            is rooted in Scripture and centered on Jesus Christ.
          </p>
        </div>
      </section>

      {/* Beliefs accordion */}
      <section className="py-20 md:py-28 px-4 md:px-6">
        <div className="max-w-[860px] mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-[34px] md:text-[46px] text-[#0a1030]"
              style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
            >
              Our Core Beliefs
            </h2>
            <p className="mt-3 text-[#5a5148] max-w-[520px] mx-auto">
              Tap any belief to read the fullness of what Scripture teaches us.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {beliefs.map((b) => {
              const Icon = b.icon;
              return (
                <AccordionItem
                  key={b.id}
                  value={b.id}
                  className="bg-white rounded-2xl border border-black/5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] overflow-hidden data-[state=open]:shadow-md transition-shadow"
                >
                  <AccordionTrigger className="px-5 md:px-7 py-5 hover:no-underline group">
                    <div className="flex items-center gap-4 md:gap-5 text-left flex-1 min-w-0">
                      <div className="h-11 w-11 md:h-12 md:w-12 flex-shrink-0 rounded-full bg-[#0a1030] text-[#e9d9a0] flex items-center justify-center">
                        <Icon className="h-5 w-5 md:h-[22px] md:w-[22px]" strokeWidth={2} />
                      </div>
                      <div className="min-w-0">
                        <div
                          className="text-[19px] md:text-[22px] leading-tight text-[#0a1030]"
                          style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
                        >
                          {b.title}
                        </div>
                        <div className="mt-1 text-[13px] md:text-[14px] text-[#6b6459] truncate">
                          {b.summary}
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 md:px-7 pb-6">
                    <div className="pl-0 md:pl-[64px]">
                      <p className="text-[15px] md:text-[16px] leading-relaxed text-[#3a3a3a]">
                        {b.body}
                      </p>
                      {b.verse && <Verse text={b.verse.text} ref={b.verse.ref} />}
                      {b.verses &&
                        b.verses.map((v, i) => (
                          <Verse key={i} text={v.text} ref={v.ref} />
                        ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </section>

      {/* Our Mission highlight */}
      <section className="px-4 md:px-6 pb-20 md:pb-28">
        <div className="max-w-[860px] mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-[#0a1030] text-white p-10 md:p-14 text-center">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#c8b98a]/10 blur-2xl" />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-[#c8b98a]/10 blur-2xl" />
            <div className="relative">
              <div className="h-14 w-14 mx-auto rounded-full bg-white/10 border border-white/20 flex items-center justify-center mb-6">
                <Compass className="h-6 w-6 text-[#c8b98a]" strokeWidth={2} />
              </div>
              <p className="text-[12px] tracking-[0.3em] uppercase text-[#c8b98a] mb-4">
                Our Mission
              </p>
              <h3
                className="text-[32px] md:text-[44px] leading-tight max-w-[720px] mx-auto"
                style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
              >
                To glorify God, proclaim the Gospel, make disciples, and love
                our community.
              </h3>
              <p className="mt-6 text-white/80 text-[16px] md:text-[17px] leading-relaxed max-w-[620px] mx-auto">
                We invite every person to experience the transforming power of
                God's presence and become part of His family.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  asChild
                  className="rounded-full bg-[#e9e3d7] hover:bg-white text-[#0a1030] px-8 h-12 text-[14px] font-medium"
                >
                  <Link to="/plan-a-visit">Plan a visit</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white px-8 h-12 text-[14px] font-medium"
                >
                  <Link to="/">Back to home</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WhatWeBelieve;
